import { IconButton } from '@mui/material'
import cn from 'classnames'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import SidenavBottomContent from 'src/components/SidenavBottomContent/SidenavBottomContent'
import SidenavContent from 'src/components/SidenavContent/SidenavContent'
import SvgLockOpenSidenav from 'src/components/Svgs/SvgLockOpenSidenav'
import { SidenavExpansionProvider } from 'src/context/SidenavItemsExpandedContext/SidenavItemsExpandedContext'
import { SupportedBrands } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useDualAnimationState } from 'src/hooks'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { SidenavContentType } from 'src/navigation/appLocations'
import { useQueryOrganizationSaasTier } from 'src/queries/organizations/saas.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { createSetShowSidenavLockedOpen } from 'src/redux/actions/app.actions'
import {
  selectIsSidenavLockedOpenFromState,
  selectIsSidenavOpenFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { useWhitelabel } from 'src/utils/whitelabel.utils'

import * as styles from './SidenavWrapper.style'

interface SidenavContainerProps {
  className?: string
  fixedElementPushdown?: number
}

const SidenavWrapper: React.FC<SidenavContainerProps> = (
  props,
): React.ReactElement => {
  const dispatch = useDispatch()
  const { data: currentUser } = useQueryOwnUser({})
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const organizationName = organization?.name || ''
  const translate = useTranslate()

  const isSidenavOpen = useSelector(selectIsSidenavOpenFromState)
  const isSidenavLockedOpen = useSelector(selectIsSidenavLockedOpenFromState)

  const {
    animationModifiers: sidenavAnimationModifiers,
    triggerFirstToSecond: sidenavTriggerFirstToSecond,
    triggerSecondToFirst: sidenavTriggerSecondToFirst,
  } = useDualAnimationState()

  useEffect((): void => {
    if (isSidenavOpen) {
      sidenavTriggerFirstToSecond()
    } else if (!isSidenavOpen) {
      sidenavTriggerSecondToFirst()
    }
  }, [isSidenavOpen])

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  const { data: organizationTier, isSuccess: isSaasTierSuccess } =
    useQueryOrganizationSaasTier({
      orgId: currentUser?.organizationId,
      enabled: !!currentUser?.organizationId,
    })
  const isStarter = organizationTier?.tier === 'STARTER'

  const { whiteLabelIncludes } = useWhitelabel()
  const isExtendBrand = whiteLabelIncludes(SupportedBrands.Extend)

  const tierLabel =
    isExtendBrand && currentUser?.hasOrgAdminPermissions && isSaasTierSuccess
      ? organizationTier?.tier === 'STARTER'
        ? 'Starter'
        : organizationTier?.tier === 'PRO'
          ? 'Pro'
          : organizationTier?.tier === 'ENTERPRISE'
            ? 'Enterprise'
            : undefined
      : undefined

  const showUpgradePlan =
    isExtendBrand && isStarter && currentUser?.hasOrgAdminPermissions

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsUserOrViewerMeCountStatistics,
      appStatisticsKeys.CreditCardsSourceCountStatistics,
    ])
  }, [dispatchStatistics])

  const [manualContentType, setManualContentType] =
    useState<SidenavContentType>(SidenavContentType.Main)

  const { contentType: locationContentType } = useExtendLocation()

  useEffect(() => {
    setManualContentType(locationContentType)
  }, [locationContentType])

  const handleShowSettings = (): void => {
    setManualContentType(SidenavContentType.Settings)
  }

  const handleShowMain = (): void => {
    setManualContentType(SidenavContentType.Main)
  }

  const toggleSidenavLock = (): void => {
    dispatch(createSetShowSidenavLockedOpen(!isSidenavLockedOpen))
  }

  return (
    <SidenavExpansionProvider>
      <div
        css={styles.sidenavContentContainer(props.fixedElementPushdown || 0)}
        className={cn({
          first: sidenavAnimationModifiers.first,
          firstToSecond: sidenavAnimationModifiers.firstToSecond,
          second: sidenavAnimationModifiers.second,
          secondToFirst: sidenavAnimationModifiers.secondToFirst,
        })}
        data-testid="sidenav"
      >
        <div
          css={styles.sidenavHeaderContent}
          className={cn({ open: isSidenavOpen })}
        >
          <div css={styles.sidenavOrgIcon}>{organizationName.charAt(0)}</div>
          <div css={styles.sidenavOrgName}>{organizationName}</div>
          <IconButton
            data-testid={'mainNavToggleLockSidenav'}
            size={'small'}
            onClick={(): void => toggleSidenavLock()}
            aria-label={translate('Toggle Sidebar')}
          >
            <SvgLockOpenSidenav />
          </IconButton>
        </div>

        <div css={styles.sidenavBody}>
          <SidenavContent
            contentType={manualContentType}
            onShowMain={handleShowMain}
            isSidenavOpen={isSidenavOpen}
          />
        </div>

        <SidenavBottomContent
          tierLabel={tierLabel}
          isSidenavOpen={isSidenavOpen}
          showUpgradePlan={showUpgradePlan}
          onShowSettings={handleShowSettings}
        />
      </div>
    </SidenavExpansionProvider>
  )
}

export default SidenavWrapper
